<template>
    <b-row>
        <b-col :cols="labelCols">
            <label :for="name" :class="{required: hasError(), 'validation-control-label': hasValidation()}" >
                {{label}}
            </label>
        </b-col>
        <b-col :cols="cols">
            <div :class="[ $options.name, { 'has-exceeded-limit': limitStatus > 100 }]">
                <div :class="`${$options.name}__htmlarea`" aria-hidden>
                    {{ valueAllowed }}
                    <em v-if="valueExcess">{{ valueExcess }}</em>
                </div>
                <textarea
                        ref="textarea"
                        :disabled="disabled"
                        :class="`${$options.name}__textarea`"
                        :maxlength="maxlength"
                        :tabindex="tabindex"
                        v-bind:value="value"
                        data-vv-validate-on="change"
                        v-validate="validate"
                        :id="name"
                        :name="name"
                        @input="updateValue"></textarea>
                <div :class="`${$options.name}__limit`">
                <span :class="`${$options.name}__remainingCharacters`">
                    {{ remainingCharacters }}
                </span>
                    <svg :class="`${$options.name}__counter`" viewBox="0 0 33.83098862 33.83098862" height="20"
                         width="20" xmlns="http://www.w3.org/2000/svg">
                        <circle :class="`${$options.name}__counterUnderlay`" cx="16.91549431" cy="16.91549431"
                                r="15.91549431" fill="none" stroke-width="2"/>
                        <circle :class="`${$options.name}__counterProgress`" :stroke-dasharray="`${limitStatus},100`"
                                cx="16.91549431" cy="16.91549431" r="15.91549431" fill="none" stroke-width="4"/>
                    </svg>
                </div>
            </div>
        </b-col>
        <div class="tooltip-container validation-error-icon" v-if="hasError()">
            <i class="fas fa-exclamation-triangle required"></i>
            <span class="tooltip-text">{{errorMessage()}}</span>
        </div>
    </b-row>
</template>

<script>
  import BaseInput from "../models/base.input";

  export default {
    name: `TweetBox`,
    extends: BaseInput,
    props: {
      maxlength: {
        type: Number,
        default: 10
      },
      tabindex: {
        type: Number,
      },
      value: {
        type: String,
        default: ''
      },
      label: {
        type: String
      },
      disabled: {
        type: Boolean,
        default: false
      },
      labelCols: {
        type: Number,
        default: 2
      },
      cols: {
        type: Number,
        default: 5
      },
      rows: {
        type: Number
      }
    },
    computed: {
      valueAllowed() {
        return this.maxlength ? this.value.slice(0, this.maxlength) : this.value;
      },
      valueExcess() {
        return this.maxlength ? this.value.slice(this.maxlength) : "";
      },
      limitStatus() {
        return this.value.length / this.maxlength * 100;
      },
      remainingCharacters() {
        return this.maxlength - this.value.length;
      },
      textareaStyle() {
        return getComputedStyle(this.$refs.textarea);
      }
    },

    mounted() {
      this.textareaGrow();
    },

  methods: {
    updateValue(e) {
      this.textareaGrow();
      this.$emit(`input`, e.target.value);
    },
    textareaGrow() {
      const paddingTop = parseInt(
        this.textareaStyle.getPropertyValue(`padding-top`),
        10
      );
      const paddingBottom = parseInt(
        this.textareaStyle.getPropertyValue(`padding-bottom`),
        10
      );
      const lineHeight = parseInt(
        this.textareaStyle.getPropertyValue(`line-height`),
        10
      );
      this.$refs.textarea.rows = this.rows || 2;
      const innerHeight =
        this.$refs.textarea.scrollHeight - paddingTop - paddingBottom;
      this.$refs.textarea.rows = innerHeight / lineHeight;
    }
  }
};
</script>

<style lang="scss">
    .TweetBox {
        $color-border: #99dde6;
        $color-danger: #e0245e;
        $color-danger-light: #ffb8c2;
        $color-gray: #657786;
        $color-gray-light: #ccd6dd;
        $color-primary: #1da1f2;
        position: relative;
        overflow: hidden;

        &__htmlarea,
        &__textarea {
            padding: 1em;
            padding-right: 3.75em; // 1
            width: 100%; // 2
            line-height: 1.25; // 2
            border: 2px solid transparent;
            border-radius: 0.5em;
        }

        &__htmlarea {
            position: absolute; // 1
            height: 100%; // 1
            background-color: #fff;
            color: transparent; // 2
            white-space: pre-wrap; // 3
            word-wrap: break-word; // 3
        }

        &__textarea {
            display: block; // 1
            position: relative;
            outline: 0;
            border: 2px solid #ced4da;
            resize: none;
        }

        &:focus {
            border-color: darken($color-border, 20%);
        }

        em {
            background: $color-danger-light;
        }

        &__limit {
            display: flex;
            position: absolute;
            right: 0.75em;
            bottom: 0.75em;
            align-items: center;

            svg:not(:root) {
                overflow: visible;
            }
        }

        &__remainingCharacters {
            margin-right: 0.5em;
            color: $color-gray;
            font-size: 0.75em;

            .has-exceeded-limit & {
                color: $color-danger;
            }
        }

        &__counter {
            overflow: visible; // 1
            transform: rotate(-90deg);
            transform-origin: center;
        }

        &__counterUnderlay {
            stroke: $color-gray-light;
        }

        &__counterProgress {
            stroke: $color-primary;

            .has-exceeded-limit & {
                stroke: $color-danger;
                animation: counterPulse 0.3s ease-in-out;
                animation-iteration-count: 1;
            }
        }

        @keyframes counterPulse {
            0% {
                stroke-width: 4;
            }
            50% {
                stroke-width: 6;
            }
            100% {
                stroke-width: 4;
            }
        }
    }
</style>
